import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Heart,
  MessageCircle,
  Filter,
  List
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import BottomNav from '@/components/BottomNav';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by category
const createCategoryIcon = (category) => {
  const colors = {
    alerte: '#ef4444',
    evenement: '#3b82f6',
    bon_plan: '#22c55e',
    info: '#f97316'
  };
  
  const color = colors[category] || '#6b7280';
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Coordinates for Réunion communes (approximate centers)
const communeCoordinates = {
  'Saint-Denis': [-20.8789, 55.4481],
  'Sainte-Marie': [-20.9167, 55.5333],
  'Sainte-Suzanne': [-20.9000, 55.6000],
  'Saint-André': [-20.9667, 55.6500],
  'Bras-Panon': [-21.0000, 55.6833],
  'Saint-Benoît': [-21.0333, 55.7167],
  'Sainte-Rose': [-21.1333, 55.7833],
  'Plaine-des-Palmistes': [-21.1333, 55.6333],
  'Saint-Pierre': [-21.3333, 55.4667],
  'Saint-Louis': [-21.2833, 55.4167],
  'Saint-Joseph': [-21.3833, 55.6167],
  'Saint-Philippe': [-21.3667, 55.7667],
  'Petite-Île': [-21.3333, 55.5667],
  'Le-Tampon': [-21.2667, 55.5167],
  'Entre-Deux': [-21.2333, 55.4667],
  'Cilaos': [-21.1333, 55.4667],
  'Étang-Salé': [-21.2667, 55.3500],
  'Les-Avirons': [-21.2333, 55.3167],
  'Saint-Paul': [-21.0000, 55.2667],
  'Saint-Leu': [-21.1667, 55.2833],
  'Trois-Bassins': [-21.1000, 55.3000],
  'La-Possession': [-20.9333, 55.3333],
  'Le-Port': [-20.9333, 55.2833],
  'Salazie': [-21.0333, 55.5167]
};

// Map center updater component
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
  }, [center, map]);
  return null;
};

const MapPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCommune, setSelectedCommune] = useState('all');
  const [mapCenter, setMapCenter] = useState([-21.1151, 55.5364]); // Center of Réunion

  const categories = [
    { id: 'all', name: 'Toutes catégories' },
    { id: 'alerte', name: 'Alertes' },
    { id: 'evenement', name: 'Événements' },
    { id: 'bon_plan', name: 'Bons Plans' },
    { id: 'info', name: 'Infos' }
  ];

  const communes = [
    { id: 'all', name: 'Toutes communes' },
    { id: 'Saint-Denis', name: 'Saint-Denis' },
    { id: 'Saint-Paul', name: 'Saint-Paul' },
    { id: 'Saint-Pierre', name: 'Saint-Pierre' },
    { id: 'Le-Tampon', name: 'Le Tampon' },
    { id: 'Saint-André', name: 'Saint-André' },
    { id: 'Saint-Louis', name: 'Saint-Louis' },
    { id: 'Saint-Benoît', name: 'Saint-Benoît' },
    { id: 'Saint-Joseph', name: 'Saint-Joseph' },
    { id: 'Sainte-Marie', name: 'Sainte-Marie' },
    { id: 'Saint-Leu', name: 'Saint-Leu' },
    { id: 'La-Possession', name: 'La Possession' },
    { id: 'Le-Port', name: 'Le Port' },
    { id: 'Sainte-Suzanne', name: 'Sainte-Suzanne' },
    { id: 'Bras-Panon', name: 'Bras-Panon' },
    { id: 'Sainte-Rose', name: 'Sainte-Rose' },
    { id: 'Saint-Philippe', name: 'Saint-Philippe' },
    { id: 'Petite-Île', name: 'Petite-Île' },
    { id: 'Cilaos', name: 'Cilaos' },
    { id: 'Salazie', name: 'Salazie' },
    { id: 'Entre-Deux', name: 'Entre-Deux' },
    { id: 'Plaine-des-Palmistes', name: 'La Plaine-des-Palmistes' },
    { id: 'Trois-Bassins', name: 'Trois-Bassins' },
    { id: 'Étang-Salé', name: "L'Étang-Salé" },
    { id: 'Les-Avirons', name: 'Les Avirons' }
  ];

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedCommune && selectedCommune !== 'all') params.append('commune', selectedCommune);
      
      const response = await api.get(`/posts?${params.toString()}`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des posts');
    } finally {
      setLoading(false);
    }
  }, [api, selectedCategory, selectedCommune]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (selectedCommune && selectedCommune !== 'all' && communeCoordinates[selectedCommune]) {
      setMapCenter(communeCoordinates[selectedCommune]);
    } else {
      setMapCenter([-21.1151, 55.5364]);
    }
  }, [selectedCommune]);

  const getCategoryLabel = (category) => {
    const labels = {
      alerte: 'Alerte',
      evenement: 'Événement',
      bon_plan: 'Bon Plan',
      info: 'Info'
    };
    return labels[category] || category;
  };

  const getCategoryStyle = (category) => {
    const styles = {
      alerte: 'badge-alerte',
      evenement: 'badge-evenement',
      bon_plan: 'badge-bon_plan',
      info: 'badge-info'
    };
    return styles[category] || '';
  };

  // Add small random offset to posts in same commune to avoid overlap
  const getPostCoordinates = (post, index) => {
    const baseCoords = communeCoordinates[post.commune] || [-21.1151, 55.5364];
    const offset = 0.005; // Small offset
    return [
      baseCoords[0] + (Math.random() - 0.5) * offset,
      baseCoords[1] + (Math.random() - 0.5) * offset
    ];
  };

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-[1000] glass border-b">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Carte</h1>
              <p className="text-xs text-muted-foreground">{posts.length} publications sur la carte</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/feed')}
              data-testid="list-view-btn"
            >
              <List className="h-4 w-4 mr-2" />
              Liste
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="flex-1" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCommune} onValueChange={setSelectedCommune}>
              <SelectTrigger className="flex-1" data-testid="commune-filter">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Commune" />
              </SelectTrigger>
              <SelectContent>
                {communes.map((commune) => (
                  <SelectItem key={commune.id} value={commune.id}>
                    {commune.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Map */}
      <main className="h-[calc(100vh-200px)]">
        {loading ? (
          <div className="h-full flex items-center justify-center bg-muted/30">
            <div className="text-center">
              <div className="spinner mb-4" />
              <p className="text-muted-foreground">Chargement de la carte...</p>
            </div>
          </div>
        ) : (
          <MapContainer 
            center={mapCenter} 
            zoom={10} 
            className="h-full w-full"
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapUpdater center={mapCenter} />
            
            {posts.map((post, index) => {
              const coords = getPostCoordinates(post, index);
              return (
                <Marker 
                  key={post.post_id}
                  position={coords}
                  icon={createCategoryIcon(post.category)}
                >
                  <Popup className="custom-popup" maxWidth={300}>
                    <Card className="border-0 shadow-none">
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={post.user_picture} />
                            <AvatarFallback className="text-xs">{post.user_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{post.user_name}</p>
                            <p className="text-xs text-muted-foreground">{post.commune}</p>
                          </div>
                          <Badge className={`${getCategoryStyle(post.category)} text-xs`}>
                            {getCategoryLabel(post.category)}
                          </Badge>
                        </div>
                        
                        <h4 className="font-bold text-sm mb-1 line-clamp-2">{post.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{post.content}</p>
                        
                        {post.image_url && (
                          <img 
                            src={post.image_url} 
                            alt={post.title}
                            className="w-full h-24 object-cover rounded-lg mb-2"
                          />
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Heart className="h-3 w-3" /> {post.likes_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" /> {post.comments_count}
                            </span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => navigate(`/post/${post.post_id}`)}
                          >
                            Voir
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </main>

      {/* Legend */}
      <div className="fixed bottom-24 left-4 bg-background/95 backdrop-blur rounded-lg p-3 shadow-lg border border-border z-[1000]">
        <p className="text-xs font-medium mb-2">Légende</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Alertes</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Événements</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Bons Plans</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span>Infos</span>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MapPage;
